import { ReactNode, useMemo } from 'react';
import {
  Table as QTable,
  TableHeaderRow,
  TableHeaderCell,
  TableRow,
  TableCell
} from 'qampo-ui-library';
import ChevronIcon from '../../assets/icons/chevron.svg?react';
import styles from './Table.module.css';

const ROW_HEIGHT = 48;

export type SortDirection = 'asc' | 'desc';

export interface TableSortState {
  key: string;
  direction: SortDirection;
}

export interface TableColumn<T> {
  key: string;
  header: ReactNode;
  render: (row: T, index: number) => ReactNode;
  width?: string | number;
  headerAlign?: 'left' | 'center' | 'right';
  cellStyle?: React.CSSProperties;
  // When true, the header becomes a sort toggle. `sortValue` provides the
  // comparable value for ordering (required for sorting to take effect).
  sortable?: boolean;
  sortValue?: (row: T) => string | number;
}

interface TableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  getRowKey?: (row: T, index: number) => string | number;
  style?: React.CSSProperties;
  footer?: ReactNode;
  getRowStyles?: (row: T, index: number) => React.CSSProperties;
  minWidth?: number | string;
  // Controlled sort state. When provided alongside sortable columns, the table
  // orders `data` by the active column's `sortValue` and renders sort toggles.
  sort?: TableSortState | null;
  onSortChange?: (key: string) => void;
}

function compareValues(a: string | number, b: string | number): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

function getCellStyles(col: TableColumn<unknown>, isHeader?: boolean) {
  const style: React.CSSProperties = {};
  if (col.width) {
    style.flexGrow = 0;
    style.flexShrink = 0;
  }

  style.color = 'var(--color-text-primary)';
  style.padding = 0;
  if (isHeader) {
    style.fontWeight = 700;

    if (col.headerAlign) {
      style.justifyContent = col.headerAlign;
    }
  } else {
    style.fontWeight = 400;
  }

  if (col.cellStyle) Object.assign(style, col.cellStyle);

  return Object.keys(style).length ? { root: style } : undefined;
}

export function Table<T>({
  columns,
  data,
  getRowKey,
  style,
  footer,
  getRowStyles,
  minWidth,
  sort,
  onSortChange
}: Readonly<TableProps<T>>) {
  const tableStyle: React.CSSProperties = {
    ...(minWidth !== undefined
      ? { minWidth: typeof minWidth === 'number' ? `${minWidth}px` : minWidth }
      : {}),
    ...style
  };

  const sortedData = useMemo(() => {
    if (!sort) return data;
    const activeColumn = columns.find((col) => col.key === sort.key);
    if (!activeColumn?.sortValue) return data;
    const getValue = activeColumn.sortValue;
    const directionFactor = sort.direction === 'asc' ? 1 : -1;
    return [...data].sort((a, b) => compareValues(getValue(a), getValue(b)) * directionFactor);
  }, [data, sort, columns]);

  const getSortIndicatorClass = (col: TableColumn<T>): string => {
    if (sort?.key !== col.key) return styles.sortInactive;
    return sort.direction === 'asc' ? styles.sortAsc : styles.sortDesc;
  };

  const renderHeader = (col: TableColumn<T>): ReactNode => {
    if (!col.sortable) return col.header;
    return (
      <button type="button" className={styles.sortButton} onClick={() => onSortChange?.(col.key)}>
        {col.header}
        <ChevronIcon className={`${styles.sortIcon} ${getSortIndicatorClass(col)}`} />
      </button>
    );
  };

  return (
    <div className={styles.scrollWrapper}>
      <QTable classes={styles.table} styles={{ root: tableStyle }}>
        <thead>
          <TableHeaderRow
            classes={styles.headerRow}
            styles={{
              root: {
                height: ROW_HEIGHT
              }
            }}
          >
            {columns.map((col) => (
              <TableHeaderCell
                key={col.key}
                width={typeof col.width === 'number' ? `${col.width}` : col.width}
                classes={styles.headerCell}
                styles={getCellStyles(col as TableColumn<unknown>, true)}
              >
                {renderHeader(col)}
              </TableHeaderCell>
            ))}
          </TableHeaderRow>
        </thead>
        <tbody style={{ gap: 0 }}>
          {sortedData.map((row, index) => (
            <TableRow
              key={getRowKey ? getRowKey(row, index) : index}
              classes={styles.row}
              styles={{
                root: {
                  height: ROW_HEIGHT,
                  borderRadius: 0,
                  borderBottom: '1px solid var(--color-border-secondary)',
                  ...(getRowStyles ? getRowStyles(row, index) : {})
                }
              }}
            >
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  width={typeof col.width === 'number' ? `${col.width}` : col.width}
                  classes={styles.cell}
                  styles={getCellStyles(col as TableColumn<unknown>)}
                >
                  {col.render(row, index)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </tbody>
        {footer && (
          <tfoot style={{ height: ROW_HEIGHT }}>
            <tr style={{ padding: 'var(--spacing-sm) var(--spacing-lg)' }}>
              <td colSpan={columns.length} className={styles.footerCell}>
                {footer}
              </td>
            </tr>
          </tfoot>
        )}
      </QTable>
    </div>
  );
}
